import { REGISTER_LABELS } from '@shared/constants/account'
import {
  type RegistrationMfaApiError,
  isNormalizedOktaAccountLockedMessage,
  isOktaInvalidPasscodeError,
  isOktaInvalidTokenError,
  isOktaMfaLockoutError,
  isOktaMfaPasscodeExpiredError,
  isOktaMfaRateLimitError,
} from './registrationMfaVerifyErrors'

export enum EMfaVerifyActionKind {
  Expired = 'expired',
  InvalidPasscode = 'invalidPasscode',
  Lockout = 'lockout',
  SessionExpired = 'sessionExpired',
  Unknown = 'unknown',
}

export type MfaVerifyAction =
  | { kind: EMfaVerifyActionKind.Expired }
  | { kind: EMfaVerifyActionKind.InvalidPasscode; nextAttemptCount: number }
  | { kind: EMfaVerifyActionKind.Lockout }
  | { kind: EMfaVerifyActionKind.SessionExpired }
  | { kind: EMfaVerifyActionKind.Unknown; message: string }

/**
 * Pure classification of Okta-shaped MFA verify errors for routing in RegisterProvider.submit.
 * Order matters: expired before invalid passcode; lockout (incl. rate limit) before invalid passcode.
 */
export function classifyMfaVerifyError(err: RegistrationMfaApiError, prevAttemptCount: number): MfaVerifyAction {
  if (isOktaMfaPasscodeExpiredError(err)) {
    return { kind: EMfaVerifyActionKind.Expired }
  }
  if (isOktaMfaLockoutError(err) || isNormalizedOktaAccountLockedMessage(err) || isOktaMfaRateLimitError(err)) {
    return { kind: EMfaVerifyActionKind.Lockout }
  }
  if (isOktaInvalidTokenError(err)) {
    return { kind: EMfaVerifyActionKind.SessionExpired }
  }
  if (isOktaInvalidPasscodeError(err)) {
    return { kind: EMfaVerifyActionKind.InvalidPasscode, nextAttemptCount: prevAttemptCount + 1 }
  }
  const message = (typeof err?.errorSummary === 'string' && err.errorSummary) || (typeof err?.message === 'string' && err.message) || REGISTER_LABELS.REGISTRATION_FAILED
  return { kind: EMfaVerifyActionKind.Unknown, message }
}
